import { constructSearchParams } from "utils/constructSearchParams";
import axiosInstance from "../utils/axiosInstance";
import { URLS } from "../utils/baseUrls";
import { ActionsDto } from "api/dtos/ActionsDto";

const getActions = ({
  fieldId,
  fieldIds,
  startDate,
  endDate,
  pageSize = 10000000,
} = {}) => {
  const hasFieldIds = Array.isArray(fieldIds)
    ? fieldIds.length > 0
    : Boolean(fieldIds);

  const params = constructSearchParams({
    pageSize,
    ...(hasFieldIds ? { fieldIds } : fieldId ? { fieldId } : {}),
    ...(startDate ? { startDate } : {}),
    ...(endDate ? { endDate } : {}),
  });

  return axiosInstance.get(`${URLS.externalRequests}/actions/`, { params });
};
const getAction = ({ actionId, fieldId }) => {
  return axiosInstance.get(
    `${URLS.externalRequests}/actions/${actionId}/?field_id=${fieldId}`,
  );
};

const postSuggestedActions = (postData) => {
  return axiosInstance.post(
    `${URLS.externalRequests}/actions-suggested/?field_id=${postData.fieldId}`,
    postData,
  );
};

const postBulkSuggestedAction = (fieldIds, action) => {
  const params = constructSearchParams({ fieldIds });
  return axiosInstance.post(
    `${URLS.externalRequests}/actions-suggested/bulk-create/`,
    {
      fieldIds,
      action,
    },
    { params },
  );
};

const postCompletedActions = (postData) => {
  return axiosInstance.post(
    `${URLS.externalRequests}/actions-performed/?field_id=${postData.fieldId}`,
    postData,
  );
};

const postBulkPerformedAction = (fieldIds, action) => {
  return axiosInstance.post(
    `${URLS.externalRequests}/actions-performed/bulk-create/`,
    {
      fieldIds,
      action,
    },
  );
};

const deleteSuggestedActions = (fieldId, actionId) => {
  return axiosInstance.delete(
    `${URLS.externalRequests}/actions-suggested/${actionId}/?field_id=${fieldId}`,
  );
};

const deleteCompletedActions = (fieldId, actionId) => {
  return axiosInstance.delete(
    `${URLS.externalRequests}/actions-performed/${actionId}/?field_id=${fieldId}`,
  );
};

const editSuggestedActions = (fieldId, actionId, editData) => {
  return axiosInstance.patch(
    `${URLS.externalRequests}/actions-suggested/${actionId}/?field_id=${fieldId}`,
    editData,
  );
};

const editCompletedActions = (fieldId, actionId, editData) => {
  return axiosInstance.patch(
    `${URLS.externalRequests}/actions-performed/${actionId}/?field_id=${fieldId}`,
    editData,
  );
};

const transferPerformedActionToSuggested = (fieldId, actionId) => {
  return axiosInstance.post(
    `${URLS.externalRequests}/actions-performed/${actionId}/transfer_to_suggested/?field_id=${fieldId}`,
    null,
  );
};

const transferSuggestedActionToPerformed = (fieldId, actionId) => {
  return axiosInstance.post(
    `${URLS.externalRequests}/actions-suggested/${actionId}/transfer_to_performed/?field_id=${fieldId}`,
    null,
  );
};

const addComment = ({ fieldId, actionId, text }) => {
  return axiosInstance.post(
    `${URLS.externalRequests}/actions/${actionId}/comments/?field_id=${fieldId}`,
    { text },
  );
};

const deleteComment = ({ fieldId, actionId, commentId }) => {
  return axiosInstance.delete(
    `${URLS.externalRequests}/actions/${actionId}/comments/${commentId}/?field_id=${fieldId}`,
  );
};

const getComments = ({ fieldId, actionId }) => {
  return axiosInstance.get(
    `${URLS.externalRequests}/actions/${actionId}/comments/?field_id=${fieldId}`,
  );
};

const getActionMedia = async ({ actionId, fieldId }) => {
  if (!actionId) {
    return [];
  }

  const params = constructSearchParams({ fieldId });
  try {
    const { data } = await axiosInstance.get(
      `${URLS.externalRequests}/actions-performed/${actionId}/attachments/`,
      { params },
    );
    const mediaData = ActionsDto.setupAllMedia(data);
    return mediaData;
  } catch (err) {
    throw err;
  }
};

const uploadActionMedia = async ({ actionId, fieldId, payload }) => {
  const params = constructSearchParams({ fieldId });

  const formData = ActionsDto.setupMediaFormData(payload);

  try {
    let res;
    if (actionId) {
      res = await axiosInstance.post(
        `${URLS.externalRequests}/actions-performed/${actionId}/attachments/upload/`,
        formData,
        { params },
      );
    } else {
      res = await axiosInstance.post(
        `${URLS.externalRequests}/actions-performed/temp-attachments/upload/`,
        formData,
      );
    }

    const newMedia = ActionsDto.setupMediaResponse(res.data);
    return newMedia;
  } catch (err) {
    throw err;
  }
};

const updateActionMedia = async ({ fileId, fieldId, payload }) => {
  const params = constructSearchParams({ fieldId });
  const formData = ActionsDto.setupMediaPatchFormData(payload);

  try {
    const res = await axiosInstance.patch(
      `${URLS.externalRequests}/actions-performed/attachments/${fileId}/update/`,
      formData,
      { params },
    );
    if (Array.isArray(res.data?.data) && res.data?.data.length > 0) {
      const updatedMedia = ActionsDto.setupMediaResponse(res.data.data[0]);
      return updatedMedia;
    }
    throw new Error("No data returned from updateActionMedia");
  } catch (err) {
    throw err;
  }
};

const deleteActionMedia = async ({ fieldId, fileId }) => {
  const params = constructSearchParams({ fieldId });
  try {
    await axiosInstance.delete(
      `${URLS.externalRequests}/actions-performed/attachments/${fileId}/delete/`,
      { params },
    );
  } catch (err) {
    throw err;
  }
};

const downloadActionMedia = async (url) => {
  try {
    const { data } = await axiosInstance.get(`${URLS.externalRequests}${url}`, {
      responseType: "blob",
    });
    return URL.createObjectURL(data);
  } catch (err) {
    throw err;
  }
};

const getFieldAttachments = async ({ fieldId }) => {
  try {
    const params = constructSearchParams({ fieldId, attachment: true });
    const res = await axiosInstance.get(
      `${URLS.externalRequests}/actions-performed/`,
      { params },
    );
    return ActionsDto.setupAllAttachments(res.data);
  } catch (err) {
    throw err;
  }
};

export const actionsApi = {
  comments: {
    fetch: getComments,
    add: addComment,
    delete: deleteComment,
  },
  deleteSuggested: deleteSuggestedActions,
  deletePerformed: deleteCompletedActions,
  fetch: getActions,
  fetchOne: getAction,
  media: {
    delete: deleteActionMedia,
    download: downloadActionMedia,
    fetchActionMedia: getActionMedia,
    fetchFieldMedia: getFieldAttachments,
    update: updateActionMedia,
    upload: uploadActionMedia,
  },
  postPerformed: postCompletedActions,
  postSuggested: postSuggestedActions,
  postBulkSuggested: postBulkSuggestedAction,
  postBulkPerformed: postBulkPerformedAction,
  transferToPerformed: transferSuggestedActionToPerformed,
  transferToSuggested: transferPerformedActionToSuggested,
  updatePerformed: editCompletedActions,
  updateSuggested: editSuggestedActions,
};
